import React, { useMemo } from 'react';
import { UserLevelSettings, UserLevelSettingsContext } from '@cko/dashboard-shared';
import {
  useGetCurrentUser,
  useGetUserProfile,
  useProcessingChannels,
  useProcessingChannelsWithMoto,
  useTimeZone,
  useUserTheme,
} from './hooks';

type UserLevelSettingsProps = {
  children: React.ReactNode;
};

export const UserLevelSettingsProvider = ({ children }: UserLevelSettingsProps) => {
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    refetch: refetchCurrentUser,
  } = useGetCurrentUser();
  const { data: userProfile, isLoading: isUserProfileLoading } = useGetUserProfile();
  const { data: channelsData, isLoading: isChannelsLoading } = useProcessingChannels();
  const { data: motoData, isLoading: isMotoChannelsLoading } = useProcessingChannelsWithMoto();
  const { timeZone, updateTimeZone } = useTimeZone(userProfile);
  const { currentTheme, resolvedTheme, updateTheme } = useUserTheme(userProfile);

  const contextValue: UserLevelSettings = useMemo(() => {
    const value: UserLevelSettings = {
      selectedTimeZone: timeZone,
      currentTheme,
      resolvedTheme,
      currentUser,
      refetchCurrentUser,
      channelsWithMoto: motoData,
      channels: channelsData,
      isLoading:
        isCurrentUserLoading || isUserProfileLoading || isChannelsLoading || isMotoChannelsLoading,
      isUserTimeZoneLoading: isUserProfileLoading,
      updateTimeZone,
      updateTheme,
    };

    return value;
  }, [
    currentUser,
    motoData,
    channelsData,
    isCurrentUserLoading,
    isUserProfileLoading,
    resolvedTheme,
    timeZone,
    currentTheme,
    updateTheme,
    updateTimeZone,
    refetchCurrentUser,
    isChannelsLoading,
    isMotoChannelsLoading,
  ]);

  return (
    <UserLevelSettingsContext.Provider value={contextValue}>
      {children}
    </UserLevelSettingsContext.Provider>
  );
};
